// prismal/blocks/timeline/src/tooltip.js
//
// A horizontally-scrolling row can never fully let a vertically-popping
// popover escape its own clipping in CSS alone: per the CSS Overflow spec,
// setting overflow-x to anything but 'visible' forces overflow-y to behave
// as 'auto' too, even if authored as 'visible' - so a card that pops up
// above its node inside .prismal-timeline-scroller will always risk being
// cropped, no matter how much padding is reserved around it.
//
// This sidesteps the problem: on hover/focus it clones the (CSS-hidden,
// no-JS-fallback) card's content into a position:fixed element appended
// straight to <body> - or the block editor iframe's own body, via
// ownerDocument - positioned from getBoundingClientRect(), so no ancestor's
// overflow can ever clip it.
//
// The tooltip also needs to survive the cursor (or keyboard focus) moving
// off the tiny dot and into the tooltip itself - e.g. to click its title,
// which links out to an in-depth page for that milestone - so hiding runs
// on a short grace-period timer that's cancelled if the node or the
// tooltip regains hover/focus in the meantime, rather than on the node's
// mouseleave/blur directly.
const DESKTOP_QUERY = '(min-width: 600px)';
const HIDE_DELAY = 250;

export const attachTimelineTooltip = (root, { isEditing = false } = {}) => {
    const scroller = root.querySelector('.prismal-timeline-scroller');
    if (!scroller) {
        return () => {};
    }

    const ownerDocument = root.ownerDocument;
    const ownerWindow = ownerDocument.defaultView || window;
    const controller = new AbortController();
    const { signal } = controller;

    scroller.classList.add('prismal-timeline--js');

    let portal = null;
    let hideTimer = null;

    const cancelHide = () => {
        if (hideTimer !== null) {
            ownerWindow.clearTimeout(hideTimer);
            hideTimer = null;
        }
    };

    const hideNow = () => {
        cancelHide();
        if (portal) {
            portal.classList.remove('is-visible');
        }
    };

    const scheduleHide = () => {
        cancelHide();
        hideTimer = ownerWindow.setTimeout(hideNow, HIDE_DELAY);
    };

    const getPortal = () => {
        if (!portal) {
            portal = ownerDocument.createElement('div');
            portal.className = 'prismal-timeline-portal';
            ownerDocument.body.appendChild(portal);

            // The hover/focus bridge: once the cursor (or focus) reaches
            // the tooltip itself, cancel any pending hide from the node
            // having just lost hover/focus to get here.
            portal.addEventListener('mouseenter', cancelHide, { signal });
            portal.addEventListener('mouseleave', scheduleHide, { signal });
            portal.addEventListener('focusout', (event) => {
                if (!event.relatedTarget || !portal.contains(event.relatedTarget)) {
                    scheduleHide();
                }
            }, { signal });
        }
        return portal;
    };

    const show = (nodeEl) => {
        if (!ownerWindow.matchMedia(DESKTOP_QUERY).matches) {
            return;
        }
        const card = nodeEl.querySelector('.prismal-timeline-node__card');
        if (!card) {
            return;
        }

        cancelHide();
        const p = getPortal();

        if (nodeEl.tagName === 'A') {
            // This milestone links out - wrap the clone in a real anchor
            // (display:contents keeps the same visual layout) so it's an
            // independently clickable/focusable link, not just inert
            // markup copied out of the (non-interactive) source card.
            const link = ownerDocument.createElement('a');
            link.className = 'prismal-timeline-portal__link';
            link.href = nodeEl.href;
            const relAttr = nodeEl.getAttribute('rel');
            if (relAttr) {
                link.rel = relAttr;
            }
            link.innerHTML = card.innerHTML;
            if (isEditing) {
                // Same reasoning as TimelineMarkup's node onClick: keep the
                // link real, but don't let clicking it navigate away from
                // the editor canvas.
                link.addEventListener('click', (event) => event.preventDefault());
            }
            p.replaceChildren(link);
        } else {
            p.innerHTML = card.innerHTML;
        }

        // Reposition (not clone) the single shared portal right after the
        // active node in the DOM - position:fixed placement is unaffected
        // by DOM location, but this keeps Tab order natural: from the
        // node, Tab reaches the tooltip's own link next, rather than
        // jumping to wherever <body> happened to already contain the
        // portal from a previous show().
        nodeEl.insertAdjacentElement('afterend', p);

        p.classList.add('is-visible');

        const nodeRect = nodeEl.getBoundingClientRect();
        const portalRect = p.getBoundingClientRect();

        let left = nodeRect.left + nodeRect.width / 2 - portalRect.width / 2;
        left = Math.max(8, Math.min(left, ownerWindow.innerWidth - portalRect.width - 8));

        let top = nodeRect.top - portalRect.height - 8;
        if (top < 8) {
            // Not enough room above (e.g. the first lane, scrolled to the
            // top) - flip below the node instead of clipping off-screen.
            top = nodeRect.bottom + 8;
        }

        p.style.left = `${left}px`;
        p.style.top = `${top}px`;
    };

    scroller.querySelectorAll('.prismal-timeline-node').forEach((nodeEl) => {
        nodeEl.addEventListener('mouseenter', () => { cancelHide(); show(nodeEl); }, { signal });
        nodeEl.addEventListener('mouseleave', scheduleHide, { signal });
        nodeEl.addEventListener('focus', () => { cancelHide(); show(nodeEl); }, { signal });
        nodeEl.addEventListener('blur', (event) => {
            if (portal && event.relatedTarget && portal.contains(event.relatedTarget)) {
                return;
            }
            scheduleHide();
        }, { signal });
    });

    // A visible tooltip is anchored to a snapshot of the node's screen
    // position - rather than tracking scroll/resize, just drop it.
    scroller.addEventListener('scroll', hideNow, { signal, passive: true });
    ownerWindow.addEventListener('resize', hideNow, { signal });

    return () => {
        controller.abort();
        cancelHide();
        if (portal && portal.parentNode) {
            portal.parentNode.removeChild(portal);
        }
        scroller.classList.remove('prismal-timeline--js');
    };
};
